import React, { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { EmptyState } from "../../components/Common";
import api from "../../lib/api";
import { isTerminalAppointment } from "../../lib/appointmentPolicy";
import { localCalendarDay } from "../../lib/localCalendarDay";
import { toast } from "sonner";
import { Calendar, ClipboardList, FileText, BarChart3, Plus, Trash2, Stethoscope, PhoneCall, AlertTriangle, CheckCircle2, X, Flag, Clock3, Coffee } from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ConsultationControl } from "../../components/ConsultationControl";
import { SlotRescheduleModal } from "../../components/AppointmentSlotPicker";
import { formatAppointmentDateTime } from "../../lib/branchTime";
import { canResumeEncounter } from "../../lib/encounterStart";
import { useCan } from "../../lib/permissions";
import { ClinicBillingWorkspace } from "../../components/ClinicBillingWorkspace";
import { VisitTodayWorkspace } from "../../components/VisitTodayWorkspace";
import { DoctorScheduleTab, DoctorLeavesTab, DoctorBlockedTab } from "../clinicadmin/DoctorModulePages";

export const links = [
  { to: "/doctor", label: "Overview", icon: <BarChart3 className="w-4 h-4" />, end: true },
  { to: "/doctor/appointments", label: "Appointments", icon: <Calendar className="w-4 h-4" /> },
  { to: "/doctor/availability", label: "Availability", icon: <Clock3 className="w-4 h-4" /> },
  { to: "/doctor/encounters", label: "Encounters", icon: <ClipboardList className="w-4 h-4" /> },
  { to: "/doctor/chains", label: "Treatment Chains", icon: <FileText className="w-4 h-4" /> },
  { to: "/doctor/patients", label: "Patients", icon: <ClipboardList className="w-4 h-4" /> },
  { to: "/doctor/followups", label: "Follow-ups", icon: <PhoneCall className="w-4 h-4" /> },
  { to: "/doctor/billing", label: "Billing", icon: <FileText className="w-4 h-4" /> },
];

export function DoctorBilling(){ return <DashboardLayout links={links} title="Billing"><ClinicBillingWorkspace /></DashboardLayout>; }

export function DoctorAvailability() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("schedule");
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/clinic/doctors-x/me/availability");
      setData(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load availability");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const tabs = [
    { key: "schedule", label: "Weekly schedule", icon: Clock3 },
    { key: "leaves", label: "Leave", icon: Coffee },
    { key: "blocked", label: "Blocked time", icon: Calendar },
  ];
  return (
    <DashboardLayout links={links} title="My Availability">
      <div className="mb-5">
        <h2 className="text-xl font-extrabold text-mg-navy">Control when patients can book you</h2>
        <p className="text-sm text-slate-500 mt-1">Your weekly schedule defines normal hours. Leave and blocked time override those hours immediately.</p>
      </div>
      <div className="mg-card p-0 overflow-x-auto mb-4">
        <div className="flex border-b border-slate-100" role="tablist" aria-label="Availability sections">
          {tabs.map(({ key, label, icon: Icon }) => <button key={key} type="button" role="tab" aria-selected={tab === key}
            onClick={() => setTab(key)} data-testid={`doctor-availability-tab-${key}`}
            className={`px-4 py-3 text-sm font-semibold inline-flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === key ? "border-mg-medical text-mg-medical" : "border-transparent text-slate-500"}`}>
            <Icon className="w-4 h-4" />{label}
          </button>)}
        </div>
      </div>
      {loading && <div className="mg-card text-center py-12 text-slate-400">Loading availability…</div>}
      {!loading && data && tab === "schedule" && <DoctorScheduleTab doctorId={data.doctor_id} schedule={data.schedule} onChange={load} selfService timezone={data.timezone} />}
      {!loading && data && tab === "leaves" && <DoctorLeavesTab doctorId={data.doctor_id} leaves={data.leaves} onChange={load} selfService />}
      {!loading && data && tab === "blocked" && <DoctorBlockedTab doctorId={data.doctor_id} blocked={data.blocked_slots} onChange={load} selfService />}
    </DashboardLayout>
  );
}

const PRIORITY_PILL = {
  urgent: "bg-red-100 text-red-700 border-red-200",
  high: "bg-amber-100 text-amber-700 border-amber-200",
  normal: "bg-slate-100 text-slate-600 border-slate-200",
  low: "bg-slate-50 text-slate-400 border-slate-100",
};

export function DoctorOverview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/doctor").then((r) => setData(r.data)); }, []);
  return (
    <DashboardLayout links={links} title="Doctor Overview">
      <VisitTodayWorkspace mode="doctor" embedded />
      {data?.doctor_profile?.doctor_id && <CalendarSubscriptionCard doctorId={data.doctor_profile.doctor_id} />}
      <MyFollowupsWidget />
    </DashboardLayout>
  );
}

function CalendarSubscriptionCard({ doctorId }) {
  const [feedUrl, setFeedUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clinic/doctors-x/${doctorId}/ical-token`);
      setFeedUrl(`${API_BASE}${r.data.feed_path}`);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [API_BASE, doctorId]);
  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    if (!window.confirm("Regenerate your calendar subscription link? Any calendar app already subscribed with the old link will stop receiving updates.")) return;
    try {
      const r = await api.post(`/clinic/doctors-x/${doctorId}/ical-token/regenerate`);
      setFeedUrl(`${API_BASE}${r.data.feed_path}`);
      toast.success("Subscription link regenerated");
    } catch (e) { toast.error("Failed to regenerate"); }
  };

  const copy = () => {
    navigator.clipboard.writeText(feedUrl);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mg-card mt-5" data-testid="doctor-calendar-subscription-card">
      <h3 className="font-bold text-mg-navy mb-1">Calendar Subscription</h3>
      <p className="text-xs text-slate-500 mb-3">Subscribe to this private link from Google Calendar, Apple Calendar, or Outlook to see your upcoming appointments automatically. Read-only — no login needed.</p>
      {loading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          <input readOnly value={feedUrl || ""} className="mg-input flex-1 text-xs font-mono" data-testid="doctor-ical-feed-url" onClick={(e) => e.target.select()} />
          <button onClick={copy} className="mg-btn-secondary text-sm shrink-0" data-testid="copy-ical-url-btn">{copied ? "Copied!" : "Copy"}</button>
          <button onClick={regenerate} className="mg-btn-secondary text-sm shrink-0 text-amber-600 border-amber-200 hover:bg-amber-50" data-testid="regenerate-ical-url-btn">Regenerate</button>
        </div>
      )}
    </div>
  );
}

function MyFollowupsWidget() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logFor, setLogFor] = useState(null); // followup record open in log modal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/clinic/followups-x/my-today?limit=5");
      setRows(r.data || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const completeOne = async (fid) => {
    try {
      await api.post(`/clinic/followups-x/${fid}/complete`, {});
      toast.success("Marked completed"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mg-card mt-5" data-testid="doc-followup-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-mg-navy flex items-center gap-2"><PhoneCall className="w-4 h-4 text-mg-medical" /> Today&apos;s pending follow-ups</h3>
        <button onClick={load} className="text-xs text-mg-medical hover:underline" data-testid="doc-followup-refresh">Refresh</button>
      </div>
      {loading && <div className="text-sm text-slate-400 py-6 text-center">Loading…</div>}
      {!loading && rows.length === 0 && (
        <EmptyState title="All clear" subtitle="No pending follow-ups assigned to you right now." icon={<CheckCircle2 className="w-6 h-6 text-emerald-500" />} />
      )}
      {!loading && rows.length > 0 && (
        <ul className="divide-y divide-slate-100" data-testid="doc-followup-list">
          {rows.map((r) => (
            <li key={r.followup_id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`doc-followup-row-${r.followup_id}`}>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${PRIORITY_PILL[r.priority] || PRIORITY_PILL.normal}`}>
                <Flag className="w-3 h-3 inline mr-0.5" />{r.priority || "normal"}
              </span>
              <div className="flex-1 min-w-[180px]">
                <div className="font-semibold text-mg-navy text-sm">{r.patient?.name || "—"} <span className="text-xs text-slate-400 ml-1">{r.patient?.phone || ""}</span></div>
                <div className="text-xs text-slate-600 line-clamp-1">{r.reason || "—"}</div>
              </div>
              <div className={`text-xs ${r.is_overdue ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                {r.is_overdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                {r.due_date ? new Date(r.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => setLogFor(r)} className="text-xs px-2 py-1 bg-mg-medical text-white rounded hover:bg-mg-medical/90" data-testid={`doc-fu-log-${r.followup_id}`}>
                  <PhoneCall className="w-3 h-3 inline" /> Log contact
                </button>
                <button onClick={() => completeOne(r.followup_id)} className="text-xs px-2 py-1 border border-emerald-200 text-emerald-700 bg-emerald-50 rounded" data-testid={`doc-fu-complete-${r.followup_id}`}>
                  <CheckCircle2 className="w-3 h-3 inline" /> Done
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {logFor && <DocLogContactModal followup={logFor} onClose={() => setLogFor(null)} onLogged={() => { setLogFor(null); load(); }} />}
    </div>
  );
}

function DocLogContactModal({ followup, onClose, onLogged }) {
  const [form, setForm] = useState({ channel: "phone", outcome: "reached", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/clinic/followups-x/${followup.followup_id}/contact`, form);
      toast.success("Contact logged"); onLogged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-mg-navy text-lg">Log contact</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Close contact form"><X className="w-5 h-5" aria-hidden="true" /></button>
        </div>
        <div className="bg-mg-medical/5 border border-mg-medical/20 rounded p-3 mb-3 text-sm">
          <div className="font-semibold text-mg-navy">{followup.patient?.name || "—"}</div>
          <div className="text-xs text-slate-500">{followup.patient?.phone || "—"} · {followup.reason}</div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Channel</label>
            <select className="mg-input w-full" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="doc-log-channel">
              <option value="phone">Phone</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
              <option value="in_person">In person</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Outcome</label>
            <select className="mg-input w-full" value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} data-testid="doc-log-outcome">
              <option value="reached">Reached</option>
              <option value="no_answer">No answer</option>
              <option value="voicemail">Voicemail / left message</option>
              <option value="declined">Declined</option>
              <option value="rescheduled">Asked to reschedule</option>
            </select>
          </div>
          <textarea className="mg-input w-full" rows={2} placeholder="Notes (optional)"
            value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="doc-log-notes" />
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
          <button onClick={submit} disabled={submitting} className="mg-btn-primary flex-1 justify-center" data-testid="doc-log-submit">
            {submitting ? "Saving…" : "Log contact"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DoctorAppointments() {
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ q: "", service: "", status: "", from: "", to: "" });
  const nav = useNavigate();
  const canReschedule = useCan("appointments.reschedule");
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [rescheduleBusy, setRescheduleBusy] = useState(false);
  const [chainChoiceFor, setChainChoiceFor] = useState(null);
  const [chainOptionsBusy, setChainOptionsBusy] = useState(false);
  useEffect(() => { api.get("/appointments").then((r) => setList(r.data)); }, []);
  const filteredAppointments = list.filter((appointment) => {
    const haystack = `${appointment.patient?.name || ""} ${appointment.patient?.phone || ""} ${appointment.patient_id || ""}`.toLowerCase();
    const day = localCalendarDay(appointment.appointment_datetime);
    return (!filters.q || haystack.includes(filters.q.toLowerCase()))
      && (!filters.service || appointment.service_id === filters.service)
      && (!filters.status || String(appointment.status || "").toLowerCase() === filters.status.toLowerCase())
      && (!filters.from || day >= filters.from) && (!filters.to || day <= filters.to);
  });
  const appointmentServices = Array.from(new Map(list.filter(a => a.service_id).map(a => [a.service_id, a.service?.name || a.service_id])).entries());
  const startEncounter = async (a) => {
    const existingId = a.encounter_id || a.encounter?.encounter_id;
    if (existingId && canResumeEncounter(a.encounter?.status || a.status)) {
      nav(`/doctor/encounter/${existingId}`);
      return;
    }
    try {
      setChainOptionsBusy(true);
      const r = await api.get(`/encounters/chain-options/${a.patient_id}`, { params: { appointment_id: a.appointment_id } });
      setChainChoiceFor({ appointment: a, options: r.data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load treatment-chain choices"); }
    finally { setChainOptionsBusy(false); }
  };
  return (
    <DashboardLayout links={links} title="My Appointments">
      <RecordFilters filters={filters} setFilters={setFilters} services={appointmentServices} />
      <div className="mg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-3">Time</th><th>Patient</th><th>Service</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {filteredAppointments.map((a) => (
              <tr key={a.appointment_id} className="border-b border-slate-50">
                <td className="py-3 font-semibold">{formatAppointmentDateTime(a)}</td>
                <td>{a.patient?.name}</td>
                <td>{a.service?.name}</td>
                <td><span className={`mg-status mg-status-${a.status.toLowerCase().replace(/\s+/g, "-")}`}>{a.status}</span></td>
                <td className="text-right">
                  {a.consultation_mode === "online" && <ConsultationControl appointmentId={a.appointment_id} />}
                  {canReschedule && ["scheduled", "confirmed"].includes(String(a.status || "").toLowerCase()) && <button type="button" onClick={() => setRescheduleFor(a)} className="text-xs border border-mg-medical text-mg-medical px-3 py-1.5 rounded-lg mr-2">Reschedule</button>}
                  {!isTerminalAppointment(a.status) && !a.encounter?.closed_at && (
                    <button disabled={chainOptionsBusy} onClick={() => startEncounter(a)} className="text-xs bg-mg-medical text-white px-3 py-1.5 rounded-lg disabled:opacity-50" data-testid={`start-encounter-${a.appointment_id}`}>
                      {(a.encounter_id || a.encounter?.encounter_id) && canResumeEncounter(a.encounter?.status || a.status) ? 'Continue encounter' : 'Start encounter'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filteredAppointments.length === 0 && <tr><td colSpan="5" className="py-8 text-center text-slate-400 text-sm">No appointments match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {rescheduleFor && <SlotRescheduleModal appointment={rescheduleFor} busy={rescheduleBusy} onClose={() => setRescheduleFor(null)} onSubmit={async (new_datetime, reason) => {
        setRescheduleBusy(true);
        try { await api.post(`/clinic/appointments-x/${rescheduleFor.appointment_id}/reschedule`, { new_datetime, reason: reason || null }); toast.success("Appointment rescheduled"); setRescheduleFor(null); const r = await api.get("/appointments"); setList(r.data); }
        catch (e) { toast.error(e?.response?.data?.detail || "Reschedule failed"); }
        setRescheduleBusy(false);
      }} />}
      {chainChoiceFor && <StartEncounterChainModal appointment={chainChoiceFor.appointment} options={chainChoiceFor.options} onClose={() => setChainChoiceFor(null)} onStarted={(encounterId) => { setChainChoiceFor(null); nav(`/doctor/encounter/${encounterId}`); }} />}
    </DashboardLayout>
  );
}

function StartEncounterChainModal({ appointment, options, onClose, onStarted }) {
  const chains = options?.chains || [];
  const [decision, setDecision] = useState("");
  const [chainId, setChainId] = useState("");
  const [newChain, setNewChain] = useState({ title: "", mainProblem: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!decision) return toast.error("Choose how this encounter should be linked");
    if (decision === "continue" && !chainId) return toast.error("Select a treatment chain");
    if (decision === "start_new" && !newChain.title.trim()) return toast.error("Enter a title for the new treatment chain");
    setBusy(true);
    try {
      const r = await api.post("/encounters", {
        appointment_id: appointment.appointment_id,
        patient_id: appointment.patient_id,
        doctor_id: appointment.doctor_id,
        chain_decision: decision,
        treatment_chain_id: decision === "continue" ? chainId : null,
        chain_title: decision === "start_new" ? newChain.title.trim() : null,
        chain_main_problem: decision === "start_new" ? newChain.mainProblem.trim() || null : null,
      });
      onStarted(r.data.encounter_id);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to start encounter"); setBusy(false); }
  };
  return <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="start-encounter-chain-title">
    <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
      <h2 id="start-encounter-chain-title" className="text-lg font-bold text-mg-navy">Choose treatment-chain context</h2>
      <p className="mt-1 text-sm text-slate-500">Review the patient’s active treatment before starting this new encounter. A suggestion is never selected automatically.</p>
      <div className="mt-5 space-y-3">
        {chains.map((chain) => <label key={chain.chain_id} className={`block cursor-pointer rounded-xl border p-4 ${chain.suggested ? "border-mg-medical bg-blue-50/60" : "border-slate-200"}`}>
          <span className="flex items-start gap-3"><input type="radio" name="chain-decision" checked={decision === "continue" && chainId === chain.chain_id} onChange={() => { setDecision("continue"); setChainId(chain.chain_id); }} className="mt-1" />
            <span><span className="font-semibold text-mg-navy">Continue: {chain.title}</span>{chain.suggested && <span className="ml-2 rounded-full bg-mg-medical px-2 py-0.5 text-xs font-semibold text-white">Suggested</span>}<span className="mt-1 block text-xs text-slate-500">{chain.main_problem || "No main problem recorded"} · {chain.branch_name || "Unknown branch"}</span></span>
          </span>
        </label>)}
        <label className="block cursor-pointer rounded-xl border border-slate-200 p-4"><span className="flex gap-3"><input type="radio" name="chain-decision" checked={decision === "start_new"} onChange={() => setDecision("start_new")} className="mt-1" /><span><span className="font-semibold text-mg-navy">Start a new treatment chain</span><span className="block text-xs text-slate-500">Use this for a distinct problem or course of treatment.</span></span></span></label>
        {decision === "start_new" && <div className="space-y-3 border-l-2 border-mg-medical/20 pl-5"><input autoFocus className="mg-input w-full" placeholder="Treatment-chain title" value={newChain.title} onChange={(e) => setNewChain({ ...newChain, title: e.target.value })} /><textarea className="mg-input w-full" rows={2} placeholder="Main problem (optional)" value={newChain.mainProblem} onChange={(e) => setNewChain({ ...newChain, mainProblem: e.target.value })} /></div>}
        {!chains.length && <label className="block cursor-pointer rounded-xl border border-slate-200 p-4"><span className="flex gap-3"><input type="radio" name="chain-decision" checked={decision === "no_chain"} onChange={() => setDecision("no_chain")} className="mt-1" /><span><span className="font-semibold text-mg-navy">Start without a treatment chain</span><span className="block text-xs text-slate-500">No active treatment chain exists for this patient.</span></span></span></label>}
      </div>
      <div className="mt-6 flex gap-2"><button onClick={onClose} disabled={busy} className="mg-btn-secondary flex-1">Cancel</button><button onClick={submit} disabled={busy || !decision} className="mg-btn-primary flex-1">{busy ? "Starting…" : "Start encounter"}</button></div>
    </div>
  </div>;
}

export function DoctorEncounters() {
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ q: "", service: "", status: "", from: "", to: "" });
  useEffect(() => { api.get("/encounters").then((r) => setList(r.data)); }, []);
  const filteredEncounters = list.filter((encounter) => {
    const haystack = `${encounter.patient?.name || ""} ${encounter.patient?.phone || ""} ${encounter.patient_id || ""}`.toLowerCase();
    const day = String(encounter.encounter_datetime || encounter.created_at || "").slice(0, 10);
    return (!filters.q || haystack.includes(filters.q.toLowerCase()))
      && (!filters.service || encounter.service_id === filters.service)
      && (!filters.status || String(encounter.status || "").toLowerCase() === filters.status.toLowerCase())
      && (!filters.from || day >= filters.from) && (!filters.to || day <= filters.to);
  });
  const encounterServices = Array.from(new Map(list.filter(e => e.service_id).map(e => [e.service_id, e.service?.name || e.service_id])).entries());
  return (
    <DashboardLayout links={links} title="Encounters">
      <RecordFilters filters={filters} setFilters={setFilters} services={encounterServices} />
      <div className="mg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-3">Date</th><th>Patient</th><th>Diagnosis</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {filteredEncounters.map((e) => (
              <tr key={e.encounter_id} className="border-b border-slate-50" data-testid={`enc-row-${e.encounter_id}`}>
                <td className="py-3 text-xs text-slate-500">{new Date(e.encounter_datetime).toLocaleString()}</td>
                <td className="font-semibold text-mg-navy">{e.patient?.name}</td>
                <td className="text-xs">{e.diagnosis || "—"}</td>
                <td><span className={`mg-status mg-status-${e.status.toLowerCase().replace(/\s+/g, "-")}`}>{e.status}</span></td>
                <td className="text-right"><Link to={`/doctor/encounter/${e.encounter_id}`} className="text-xs text-mg-medical font-semibold">Open →</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

function RecordFilters({ filters, setFilters, services = [] }) {
  const change = (key, value) => setFilters(current => ({ ...current, [key]: value }));
  return <div className="mg-card mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
    <label className="text-xs font-bold text-slate-500">Patient<input className="mg-input w-full mt-1" placeholder="Name, mobile or patient ID" value={filters.q} onChange={e => change("q", e.target.value)} /></label>
    <label className="text-xs font-bold text-slate-500">Service<select className="mg-input w-full mt-1" value={filters.service} onChange={e => change("service", e.target.value)}><option value="">All services</option>{services.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></label>
    <label className="text-xs font-bold text-slate-500">Status<select className="mg-input w-full mt-1" value={filters.status} onChange={e => change("status", e.target.value)}><option value="">All statuses</option>{["Draft","In Progress","Scheduled","Confirmed","Checked In","waiting_for_doctor","in_consultation","Open","Completed","Closed","Cancelled","No Show","Expired"].map(status => <option key={status} value={status}>{status.replaceAll("_", " ")}</option>)}</select></label>
    <label className="text-xs font-bold text-slate-500">From<input type="date" className="mg-input w-full mt-1" value={filters.from} onChange={e => change("from", e.target.value)} /></label>
    <label className="text-xs font-bold text-slate-500">To<input type="date" className="mg-input w-full mt-1" value={filters.to} onChange={e => change("to", e.target.value)} /></label>
  </div>;
}

export function DoctorChains() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/treatment-chains").then((r) => setList(r.data)); }, []);
  return (
    <DashboardLayout links={links} title="Treatment Chains">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((c) => (
          <div key={c.chain_id} className="mg-card" data-testid={`chain-${c.chain_id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-bold text-mg-navy">{c.title}</div>
                <div className="text-xs text-slate-500">{c.patient?.name}</div>
              </div>
              <span className={`mg-status mg-status-${c.status.toLowerCase()}`}>{c.status}</span>
            </div>
            <p className="text-xs text-slate-600 mt-2">{c.main_problem}</p>
            <div className="text-xs text-slate-500 mt-2">{c.encounter_count} encounters</div>
          </div>
        ))}
        {list.length === 0 && <EmptyState title="No treatment chains yet" message="Chains group multiple encounters for the same medical issue." />}
      </div>
    </DashboardLayout>
  );
}

export function DoctorPatients() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/clinic/patients", { params: { q } }).then((r) => setList(r.data)); }, [q]);
  return (
    <DashboardLayout links={links} title="Patients">
      <input className="mg-input mb-4 max-w-md" placeholder="Search patients…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="mg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-3">Name</th><th>Phone</th><th>Gender</th></tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.patient_id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="py-3 font-semibold text-mg-navy"><Link to={`/doctor/patients/${p.patient_id}`} className="hover:text-mg-medical hover:underline">{p.name}</Link></td>
                <td>{p.phone || "—"}</td>
                <td>{p.gender || "—"}</td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="3" className="py-8 text-center text-slate-400">No patients found.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
